/**
 * BrandingContext — hydrates the global brand identity from the backend
 * once at app boot (or on demand via `refresh()`), then broadcasts it to
 * every component through the `useBranding()` hook.
 *
 * Any component that renders the platform name, logo, WhatsApp CTA, or the
 * login background pulls those values from here. The context is populated
 * via `GET /api/branding` (public) so the login page renders correctly
 * before the user signs in.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BRAND = {
  brand_name: "My GlobalTopup",
  brand_tagline: "Reseller Portal",
  whatsapp_number: "56959290754",
  brand_logo_url: null,
  brand_login_image_url: null,
  has_logo: false,
  has_login_image: false,
  landing_stats_section_enabled: true,
  landing_stats: [],
};

const BrandingContext = createContext({ brand: DEFAULT_BRAND, refresh: async () => {} });

function absolutize(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export function BrandingProvider({ children }) {
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branding`, { timeout: 8000 });
      setBrand({
        ...DEFAULT_BRAND,
        ...data,
        brand_logo_url: absolutize(data.brand_logo_url),
        brand_login_image_url: absolutize(data.brand_login_image_url),
      });
    } catch (e) {
      // Silent fail — keep defaults. This is expected on cold-start until
      // the backend responds; UI degrades gracefully to the "My GlobalTopup"
      // text-only brand.
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Reflect the brand name in the browser title so it updates the tab
  // label + PWA name without redeploying.
  useEffect(() => {
    if (typeof document !== "undefined" && brand?.brand_name) {
      document.title = brand.brand_name;
    }
  }, [brand?.brand_name]);

  return (
    <BrandingContext.Provider value={{ brand, refresh }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

export function whatsappHref(number, message = "") {
  const digits = String(number || "").replace(/\D/g, "");
  const msg = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${digits}${msg}`;
}
